import { Button, Input, Label, ListBox, Select, Spinner, TextField } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowUpRightIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { type FormEntries, useSubmitForm } from 'src/api/mutations';
import { Container } from 'src/components/container';
import { FadeIn } from 'src/components/fade-in';

const formSchema = z.object({
  firstName: z.string().min(1, 'El nombre es obligatorio'),
  lastName: z.string().min(1, 'El apellido es obligatorio'),
  numberOfGuests: z.number(),
});

export const Route = createFileRoute('/form')({
  component: RouteComponent,
});

function RouteComponent() {
  const submitForm = useSubmitForm();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormEntries>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      numberOfGuests: 0,
    },
  });

  const onSubmit = (data: FormEntries) => submitForm.mutate(data);

  const navigate = useNavigate();

  useEffect(() => {
    if (submitForm.isSuccess) {
      navigate({ to: '/thanks' });
    }
  }, [submitForm.isSuccess, navigate]);

  return (
    <section className="py-22 md:py-42">
      <Container>
        <FadeIn>
          <h1 className="text-4xl leading-tight">
            Confirmá tu asistencia.
            <br />
            ¡Nos vemos pronto!
          </h1>
        </FadeIn>

        <FadeIn delay={0.4}>
          <form
            className="mt-12 flex flex-col gap-6 bg-white p-6 md:p-10"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-4 md:flex-row [&>*]:flex-1">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField isInvalid={!!errors.firstName}>
                    <Label>Nombre</Label>
                    <Input
                      placeholder="John"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                    {errors.firstName && (
                      <span className="text-danger text-sm">{errors.firstName.message}</span>
                    )}
                  </TextField>
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField isInvalid={!!errors.lastName}>
                    <Label>Apellido</Label>
                    <Input
                      placeholder="Doe"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                    {errors.lastName && (
                      <span className="text-danger text-sm">{errors.lastName.message}</span>
                    )}
                  </TextField>
                )}
              />
            </div>

            <Controller
              name="numberOfGuests"
              control={control}
              render={({ field }) => (
                <div>
                  <Select
                    placeholder="Elegir uno"
                    selectedKey={String(field.value)}
                    onSelectionChange={(key) => field.onChange(Number(key))}
                  >
                    <Label>¿Cuántos más invitados se sumarán con vos?</Label>
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        {[0, 1, 2, 3, 4].map((n) => (
                          <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                            {n}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>
              )}
            />

            <Button fullWidth={true} type="submit" isPending={submitForm.isPending}>
              {submitForm.isPending && <Spinner size="sm" color="current" />}
              {submitForm.isPending ? 'Enviando...' : 'Enviar'}
              {!submitForm.isPending && <ArrowUpRightIcon />}
            </Button>
          </form>
        </FadeIn>
      </Container>
    </section>
  );
}
